import React from 'react';
import {
  useCreateRemoveRetailPriceRangeFilters,
  removeDspClicks
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useMetricByWeekId } from 'src/serverProxy/useMetricByWeekId';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TotalTrafficDonutChart from './TotalTrafficDonutChart';
import { TrafficBarCharts } from './TrafficBarCharts';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { onDisplayAdvertisingMode } from 'src/store/modules/user/selectors';
import { useAppSelector } from 'src/utils/Hooks';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';

/**
 * Shows donut chart and bar charts for the top section of Total Traffic
 */
export const TotalTrafficTopSection = () => {
  const mainEntityType = useMainEntityType();
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  // TODO: Use agg-traffic index when available for topics
  const isAdvertisingDisplayMode = useAppSelector(onDisplayAdvertisingMode);
  const index = mainEntityType === 'searchtermlist' || !isAdvertisingDisplayMode ? 'traffic' : 'aggregated-traffic';

  const { primaryTotal: totalClicks, isLoading: totalLoading } = useMetricByWeekId({
    indexName: index,
    fieldName: 'totalClicks',
    requestBuilder: (builder: AdvancedSearchRequestBuilder) => {
      return removeDspClicks(removeRetailPriceRangeFilters()(builder), !isAdvertisingDisplayMode);
    }
  });
  const {
    primaryTotal: organicClicks,
    secondaryTotal: organicClicksComparison,
    isLoading: organicLoading
  } = useMetricByWeekId({
    indexName: index,
    fieldName: 'organicClicks',
    requestBuilder: (builder: AdvancedSearchRequestBuilder) => {
      return removeDspClicks(removeRetailPriceRangeFilters()(builder), !isAdvertisingDisplayMode);
    }
  });
  const {
    primaryTotal: paidTraffic,
    secondaryTotal: paidTrafficComparison,
    isLoading: paidLoading
  } = useMetricByWeekId({
    indexName: index,
    fieldName: 'adClicks',
    requestBuilder: (builder: AdvancedSearchRequestBuilder) => {
      return removeDspClicks(removeRetailPriceRangeFilters()(builder), !isAdvertisingDisplayMode);
    }
  });
  const {
    primaryTotal: otherTraffic,
    secondaryTotal: otherTrafficComparison,
    isLoading: otherLoading
  } = useMetricByWeekId({
    indexName: index,
    fieldName: 'otherClicks',
    requestBuilder: (builder: AdvancedSearchRequestBuilder) => {
      return removeDspClicks(removeRetailPriceRangeFilters()(builder), !isAdvertisingDisplayMode);
    }
  });
  const { primaryTotal: dspClicks, isLoading: dspLoading } = useMetricByWeekId({
    indexName: 'advertisingDisplay',
    fieldName: 'dpv',
    requestBuilder: (builder: AdvancedSearchRequestBuilder) => {
      return builder.apply(removeRetailPriceRangeFilters());
    }
  });

  const loading = organicLoading || paidLoading || otherLoading || dspLoading || totalLoading;

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <TotalTrafficDonutChart
        loading={loading}
        dspClicks={dspClicks}
        organicClicks={organicClicks}
        otherTraffic={otherTraffic}
        paidTraffic={paidTraffic}
        totalClicks={totalClicks}
      />
      {shouldShowBeaconProTopics() ? (
        <TrafficBarCharts
          loading={loading}
          organicTraffic={{
            comparison: organicClicksComparison,
            current: organicClicks
          }}
          otherTraffic={{
            comparison: otherTrafficComparison,
            current: otherTraffic
          }}
          paidTraffic={{
            comparison: paidTrafficComparison,
            current: paidTraffic
          }}
        />
      ) : (
        <div
          style={{
            width: '624px'
          }}
        >
          <BeaconComparableSplineChart
            indexName={mainEntityType === 'searchtermlist' ? 'traffic' : 'aggregated-traffic'} // TODO: Use agg-traffic index when available for topics
            fieldName="totalClicks"
            showTitleSummary={false}
            splineChartLoadingProps={{
              width: 543
            }}
            chartOptionsOverride={{
              chart: {
                height: 300
              }
            }}
            requestBuilder={removeRetailPriceRangeFilters()}
          />
        </div>
      )}
    </Flex>
  );
};
